import { addDays, format, getDay, parseISO } from "date-fns";
import { formatInTimeZone, fromZonedTime } from "date-fns-tz";

import { validTimeZoneOrUtc } from "../timeUtils.js";

/** Minimum ranked members before competitive comparison copy is meaningful. */
export const MIN_COHORT_SIZE = 10;

export interface ProductivityComparisonUserStats {
  points: number;
  sessionCount: number;
  durationMinutes: number;
}

export interface ProductivityComparisonCohortInput {
  avgPoints: number;
  avgSessionCount: number;
  avgDurationMinutes: number;
}

export interface ProductivityComparisonDeltas {
  pointsVsAvg: number;
  pointsVsAvgPercent: number | null;
  sessionCountVsAvg: number;
  durationMinutesVsAvg: number;
}

export type ProductivityComparisonMetric =
  | "points"
  | "sessionCount"
  | "durationMinutes";

/**
 * Precomputed deltas between a member and cohort averages.
 */
export function computeComparisonDeltas(
  user: ProductivityComparisonUserStats,
  cohort: ProductivityComparisonCohortInput
): ProductivityComparisonDeltas {
  const pointsVsAvg = user.points - cohort.avgPoints;
  const pointsVsAvgPercent =
    cohort.avgPoints === 0
      ? null
      : (pointsVsAvg / cohort.avgPoints) * 100;

  return {
    pointsVsAvg,
    pointsVsAvgPercent,
    sessionCountVsAvg: user.sessionCount - cohort.avgSessionCount,
    durationMinutesVsAvg: user.durationMinutes - cohort.avgDurationMinutes,
  };
}

/**
 * Upper bound of "top X%" placement (1 = best). Lower values are better ranks.
 */
export function computeTopPercentCeiling(
  rank: number,
  totalRanked: number
): number {
  if (totalRanked <= 0 || rank <= 0) {
    return 100;
  }
  return Math.max(1, Math.min(100, Math.ceil((100 * rank) / totalRanked)));
}

export function meetsMinimumCohortSize(
  cohortSize: number,
  min: number = MIN_COHORT_SIZE
): boolean {
  return cohortSize >= min;
}

/**
 * Percent delta of a value vs cohort average. Returns null when average is zero.
 */
export function computeMetricPercentDelta(
  value: number,
  average: number
): number | null {
  if (average === 0) {
    return null;
  }
  return ((value - average) / average) * 100;
}

/** True when the member beats the cohort average on the chosen metric. */
export function isAboveAverage(
  deltas: ProductivityComparisonDeltas,
  metric: ProductivityComparisonMetric
): boolean {
  switch (metric) {
    case "points":
      return deltas.pointsVsAvg > 0;
    case "sessionCount":
      return deltas.sessionCountVsAvg > 0;
    case "durationMinutes":
      return deltas.durationMinutesVsAvg > 0;
  }
}

/** Top tier by rank percentile (default: top 25%). */
export function isTopTier(
  topPercentCeiling: number,
  threshold = 25
): boolean {
  return topPercentCeiling <= threshold;
}

/** Lower tier by rank percentile (default: worse than top 65%, i.e. bottom ~35%+). */
export function isLowerTier(
  topPercentCeiling: number,
  threshold = 65
): boolean {
  return topPercentCeiling > threshold;
}

// ---------------------------------------------------------------------------
// Weekly focus benchmark (TX-4793): "where you stand" on /productivity.
// ---------------------------------------------------------------------------

/**
 * Members a weekly comparison needs before it says anything about other people.
 * Higher than the daily {@link MIN_COHORT_SIZE}: a week's cohort is larger, so the
 * floor costs little and makes a percentile honest.
 */
export const MIN_WEEKLY_COHORT_SIZE = 30;

/** The last full Sunday–Saturday week in a member's time zone. */
export interface LastFullWeekBounds {
  /** Sunday, `yyyy-MM-dd`, member's calendar. */
  startYmd: string;
  /** Saturday, `yyyy-MM-dd`, member's calendar (inclusive). */
  endYmd: string;
  /** Sunday 00:00 local, as an instant. */
  startUtc: Date;
  /** The following Sunday 00:00 local, as an instant (exclusive). */
  endExclusiveUtc: Date;
}

/**
 * The Sunday–Saturday week before the current one, in the member's time zone.
 *
 * Each boundary is a local midnight converted on its own, so a week that crosses a
 * daylight-saving change is 167 or 169 hours long, as it is on the member's clock.
 * A stored zone the runtime does not know is read as UTC: the settings API keeps
 * any string, and `formatInTimeZone` throws RangeError on an unknown one.
 */
export function getLastFullWeekBounds(
  now: Date,
  timezone: string | null | undefined
): LastFullWeekBounds {
  const { tz, thisSunday } = localWeek(now, timezone);
  return weekFrom(addDays(thisSunday, -7), tz);
}

/** This Sunday–Saturday week, still running, in a member's time zone (TX-5328). */
export interface ThisWeekBounds extends LastFullWeekBounds {
  /** Days left in the week, today included: 7 on a Sunday, 1 on a Saturday. */
  daysLeft: number;
}

/** The week the member is in now, on their own calendar. */
export function getThisWeekBounds(
  now: Date,
  timezone: string | null | undefined
): ThisWeekBounds {
  const { tz, todayNoon, thisSunday } = localWeek(now, timezone);
  return { ...weekFrom(thisSunday, tz), daysLeft: 7 - getDay(todayNoon) };
}

function localWeek(now: Date, timezone: string | null | undefined) {
  const tz = validTimeZoneOrUtc(timezone);
  // Noon keeps date arithmetic away from any midnight edge.
  const todayNoon = parseISO(`${formatInTimeZone(now, tz, "yyyy-MM-dd")}T12:00:00`);
  return { tz, todayNoon, thisSunday: addDays(todayNoon, -getDay(todayNoon)) };
}

/** The Sunday–Saturday week starting on `sundayNoon`. */
function weekFrom(sundayNoon: Date, tz: string): LastFullWeekBounds {
  const startYmd = formatYmd(sundayNoon);
  const nextYmd = formatYmd(addDays(sundayNoon, 7));
  return {
    startYmd,
    endYmd: formatYmd(addDays(sundayNoon, 6)),
    startUtc: fromZonedTime(`${startYmd}T00:00:00`, tz),
    endExclusiveUtc: fromZonedTime(`${nextYmd}T00:00:00`, tz),
  };
}

function formatYmd(localNoon: Date): string {
  return format(localNoon, "yyyy-MM-dd");
}

/**
 * "More than X% of members": the share of the cohort strictly below you, rounded
 * down, and never 100 — you are in the cohort yourself.
 */
export function computeMoreThanPercent(belowCount: number, cohortSize: number): number {
  if (cohortSize <= 0 || belowCount <= 0) return 0;
  return Math.min(99, Math.floor((100 * belowCount) / cohortSize));
}

/**
 * Where a member stands against the typical member. Below the median there is no
 * percentile at all — the member sees the typical number instead, never a
 * "bottom X%" (Tony, 2026-09-27).
 *
 * `aboutTypical` is at or above the median with fewer than half the cohort
 * strictly below: a tie at the median, which as a percentile would read "more
 * than 0%" (or 33%) of members.
 */
export type WeeklyStanding = (
  | { standing: "atOrAbove"; moreThanPercent: number }
  | { standing: "aboutTypical" }
  | { standing: "below" }
) & {
  typicalMinutes: number;
  /** Where the top 25% of the cohort starts, to {@link TYPICAL_MINUTES_STEP} (TX-5328). */
  topQuarterMinutes: number;
};

/**
 * The typical member's minutes are shown to this step. An odd cohort's median is
 * one member's exact week; rounded, it no longer is.
 */
export const TYPICAL_MINUTES_STEP = 5;

export interface WeeklyStandingInput {
  minutes: number;
  medianMinutes: number;
  /** Cohort members with strictly fewer minutes than this member. */
  belowCount: number;
  cohortSize: number;
  /** The cohort's 75th percentile of minutes. */
  topQuarterMinutes: number;
}

/** `null` under the {@link MIN_WEEKLY_COHORT_SIZE} floor: too few members to compare. */
export function resolveWeeklyStanding(input: WeeklyStandingInput): WeeklyStanding | null {
  if (!meetsMinimumCohortSize(input.cohortSize, MIN_WEEKLY_COHORT_SIZE)) return null;
  const typicalMinutes = toStep(input.medianMinutes);
  const topQuarterMinutes = toStep(input.topQuarterMinutes);
  if (input.minutes >= input.medianMinutes) {
    const moreThanPercent = computeMoreThanPercent(input.belowCount, input.cohortSize);
    if (moreThanPercent < 50) return { standing: "aboutTypical", typicalMinutes, topQuarterMinutes };
    return { standing: "atOrAbove", moreThanPercent, typicalMinutes, topQuarterMinutes };
  }
  return { standing: "below", typicalMinutes, topQuarterMinutes };
}

function toStep(minutes: number): number {
  return Math.round(minutes / TYPICAL_MINUTES_STEP) * TYPICAL_MINUTES_STEP;
}

/** `GET /v1/sessions/productivity/benchmark` — never carries another member's row. */
export interface WeeklyFocusBenchmark {
  week: { start: string; end: string };
  you: { focusedMinutes: number; daysFocused: number };
  comparison: WeeklyStanding | null;
  /** The member's own week so far, live (TX-5328). Never anyone else's. */
  thisWeek: { start: string; end: string; focusedMinutes: number; daysLeft: number };
}

/** Who this week's minutes are measured against: last week's marks, in order. */
export type WeeklyChaseTarget = "typical" | "topQuarter" | "lastWeek";

/**
 * "Where you stand" as a chase (TX-5328): this week's minutes against the next
 * mark still ahead — the typical member, then the top 25%; under the cohort
 * floor, the member's own last week.
 */
export type WeeklyChase =
  | {
      kind: "chasing";
      target: WeeklyChaseTarget;
      minutes: number;
      targetMinutes: number;
      remainingMinutes: number;
      /** What is left spread over the days left, up to the next 5 minutes. */
      perDayMinutes: number;
      daysLeft: number;
    }
  | {
      kind: "beaten";
      target: WeeklyChaseTarget;
      minutes: number;
      targetMinutes: number;
      daysLeft: number;
    }
  | { kind: "open"; minutes: number; daysLeft: number };

export function resolveWeeklyChase(benchmark: WeeklyFocusBenchmark): WeeklyChase {
  const { focusedMinutes: minutes, daysLeft } = benchmark.thisWeek;
  const marks = chaseMarks(benchmark);
  const next = marks.find((mark) => minutes < mark.targetMinutes);
  if (next) {
    const remainingMinutes = next.targetMinutes - minutes;
    const perDay = remainingMinutes / Math.max(1, daysLeft);
    return {
      kind: "chasing",
      ...next,
      minutes,
      remainingMinutes,
      perDayMinutes: Math.max(
        TYPICAL_MINUTES_STEP,
        Math.ceil(perDay / TYPICAL_MINUTES_STEP) * TYPICAL_MINUTES_STEP
      ),
      daysLeft,
    };
  }
  const last = marks[marks.length - 1];
  if (last) return { kind: "beaten", ...last, minutes, daysLeft };
  return { kind: "open", minutes, daysLeft };
}

function chaseMarks(
  benchmark: WeeklyFocusBenchmark
): { target: WeeklyChaseTarget; targetMinutes: number }[] {
  const { comparison } = benchmark;
  if (!comparison) {
    const lastWeek = benchmark.you.focusedMinutes;
    return lastWeek > 0 ? [{ target: "lastWeek", targetMinutes: lastWeek }] : [];
  }
  const marks: { target: WeeklyChaseTarget; targetMinutes: number }[] = [];
  if (comparison.typicalMinutes > 0) {
    marks.push({ target: "typical", targetMinutes: comparison.typicalMinutes });
  }
  if (comparison.topQuarterMinutes > comparison.typicalMinutes) {
    marks.push({ target: "topQuarter", targetMinutes: comparison.topQuarterMinutes });
  }
  return marks;
}
